import { useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { SignInMethodOption, SignInVerification } from '../../../../entities/SignInMethod';
import { ResendFeedback } from '../../../../entities/Login';
import { useOtpVerifyMutation, useSingInMethodMutation } from '../../../../services/singnin.service';
import { cookieShareDomain } from '../../../../services/api.service';
import { dynamicCustomEventTracking } from '../../../../lib/gtm';
import CodeStepModel from './model';

type CodeStepViewModelProps = {
  verifyData: SignInVerification;
  method: SignInMethodOption;
  onVerified: (token: string) => void;
  onLoadingChange: (isLoading: boolean) => void;
  onContinueWithoutLogin: () => void;
  onMethodChanged: (method: SignInMethodOption) => void;
};

export default function CodeStepViewModel({
  verifyData,
  method,
  onVerified,
  onLoadingChange,
  onContinueWithoutLogin,
  onMethodChanged
}: CodeStepViewModelProps) {
  const model = CodeStepModel;
  const [, setCookie] = useCookies(['oya_oyana_access_token']);
  const [showInvalid, setShowInvalid] = useState(false);
  const [resendFeedback, setResendFeedback] = useState<ResendFeedback | null>(null);
  const [pendingMethod, setPendingMethod] = useState<SignInMethodOption | null>(null);

  const alternativeMethod = useMemo(() => model.getAlternativeMethod(verifyData, method), [verifyData, method]);

  const [otpVerify, { data: dataOTP, error: errorOTP, isLoading: isVerifying, isSuccess: isVerified }] =
    useOtpVerifyMutation();
  const [singInMethod, { data: dataResend, error: errorResend, isLoading: isResending }] = useSingInMethodMutation();

  useEffect(() => {
    onLoadingChange(isVerifying || isResending);
  }, [isVerifying, isResending]);

  useEffect(() => {
    if (errorOTP) {
      dynamicCustomEventTracking.send({ dynamicEventName: 'journey_quick_access_error_otp_ag' });
      setShowInvalid(true);
    }
  }, [errorOTP]);

  useEffect(() => {
    if (dataOTP && dataOTP.token) {
      setCookie('oya_oyana_access_token', dataOTP.token, {
        domain: cookieShareDomain,
        secure: process.env.REACT_APP_STAGE !== 'local',
        maxAge: 86400
      });
      onVerified(dataOTP.token);
    }
  }, [dataOTP]);

  useEffect(() => {
    if (!dataResend) return;
    setResendFeedback(ResendFeedback.SUCCESS);
    if (pendingMethod) {
      onMethodChanged(pendingMethod);
      setPendingMethod(null);
    }
  }, [dataResend]);

  useEffect(() => {
    if (!errorResend) return;
    setResendFeedback(ResendFeedback.ERROR);
    setPendingMethod(null);
  }, [errorResend]);

  const onCodeChange = (code: string) => {
    setShowInvalid(false);
    if (model.isComplete(code) && !isVerifying && !isVerified) {
      otpVerify({ oyanaid: verifyData.oyanaid, code });
    }
  };

  const handleResend = () => {
    dynamicCustomEventTracking.send({ dynamicEventName: `journey_quick_access_${method.type}_retry_ag` });
    setResendFeedback(null);
    singInMethod({ id: verifyData.oyanaid, method: method.type });
  };

  const handleSwitchMethod = () => {
    if (!alternativeMethod) return;
    dynamicCustomEventTracking.send({ dynamicEventName: `journey_quick_access_${alternativeMethod.type}_ag` });
    setResendFeedback(null);
    setShowInvalid(false);
    setPendingMethod(alternativeMethod);
    singInMethod({ id: verifyData.oyanaid, method: alternativeMethod.type });
  };

  const handleContinueWithoutCode = () => {
    dynamicCustomEventTracking.send({ dynamicEventName: 'journey_quick_access_continue_without_login_ag' });
    onContinueWithoutLogin();
  };

  return {
    destination: method.value,
    hasError: showInvalid,
    errorMessage: model.INVALID_CODE_MESSAGE,
    isVerified,
    isResending,
    resendMessage: model.getResendMessage(resendFeedback),
    isResendError: resendFeedback === ResendFeedback.ERROR,
    switchMethodLabel: alternativeMethod ? model.getSwitchMethodLabel(alternativeMethod.type) : null,
    onCodeChange,
    handleResend,
    handleSwitchMethod,
    handleContinueWithoutCode
  };
}
